import React from 'react';
import { Search, Inbox, CheckCircle2, Layers, Plus, Settings, HelpCircle, ChevronDown, Edit } from 'lucide-react';
export type NavKey = 'inbox' | 'my-issues' | 'views';
interface SidebarProps {
  onNewIssue: () => void;
  activeNav: NavKey;
  onNavChange: (nav: NavKey) => void;
}
export function Sidebar({
  onNewIssue,
  activeNav,
  onNavChange
}: SidebarProps) {
  return <div className="w-[240px] flex-shrink-0 bg-[#f7f8f9] border-r border-[#eef0f2] h-full flex flex-col text-[13px] text-[#5e6ad2]" data-id="element-150">
      {/* Workspace Header */}
      <div className="h-12 flex items-center px-4 hover:bg-[#edeef0] cursor-pointer transition-colors mt-1" data-id="element-151">
        <div className="w-4 h-4 rounded bg-[#5e6ad2] text-white flex items-center justify-center font-bold text-[10px] mr-2.5 shadow-sm" data-id="element-152">
          M
        </div>
        <span className="font-medium text-slate-800 flex-1 truncate" data-id="element-153">
          Magic Patterns
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-154" />
      </div>

      {/* Action Buttons */}
      <div className="px-3 py-2 space-y-0.5" data-id="element-155">
        <button onClick={onNewIssue} className="w-full flex items-center px-2 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-[#edeef0] rounded-md transition-colors group" data-id="element-156">
          <Edit className="w-3.5 h-3.5 mr-2.5 text-slate-400 group-hover:text-slate-600" data-id="element-157" />
          <span className="font-medium" data-id="element-158">New Issue</span>
          <span className="ml-auto text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white shadow-sm" data-id="element-159">
            C
          </span>
        </button>
        <button className="w-full flex items-center px-2 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-[#edeef0] rounded-md transition-colors group" data-id="element-160">
          <Search className="w-3.5 h-3.5 mr-2.5 text-slate-400 group-hover:text-slate-600" data-id="element-161" />
          <span className="font-medium" data-id="element-162">Search</span>
          <span className="ml-auto text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white shadow-sm" data-id="element-163">
            ⌘K
          </span>
        </button>
      </div>

      {/* Primary Nav */}
      <div className="px-3 py-2 space-y-0.5" data-id="element-164">
        <NavItem icon={<Inbox className="w-3.5 h-3.5" data-id="element-166" />} label="Inbox" active={activeNav === 'inbox'} onClick={() => onNavChange('inbox')} data-id="element-165" />
        <NavItem icon={<CheckCircle2 className="w-3.5 h-3.5" data-id="element-168" />} label="My Issues" active={activeNav === 'my-issues'} onClick={() => onNavChange('my-issues')} data-id="element-167" />
        <NavItem icon={<Layers className="w-3.5 h-3.5" data-id="element-170" />} label="Views" active={activeNav === 'views'} onClick={() => onNavChange('views')} data-id="element-169" />
      </div>

      {/* Teams */}
      <div className="mt-4 px-3" data-id="element-171">
        <div className="px-2 text-[11px] font-semibold text-slate-400 mb-1.5 hover:text-slate-500 cursor-pointer flex items-center justify-between group" data-id="element-172">
          Your Teams
          <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-173" />
        </div>
        <div className="space-y-0.5" data-id="element-174">
          <NavItem icon={<div className="w-3.5 h-3.5 rounded bg-orange-100 text-orange-600 flex items-center justify-center text-[9px] font-bold" data-id="element-176">
                E
              </div>} label="Engineering" data-id="element-175" />
          <NavItem icon={<div className="w-3.5 h-3.5 rounded bg-purple-100 text-purple-600 flex items-center justify-center text-[9px] font-bold" data-id="element-178">
                D
              </div>} label="Design" data-id="element-177" />
          <NavItem icon={<div className="w-3.5 h-3.5 rounded bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] font-bold" data-id="element-180">
                P
              </div>} label="Product" data-id="element-179" />
        </div>
      </div>

      <div className="mt-auto px-3 py-3 space-y-0.5" data-id="element-181">
        <NavItem icon={<Settings className="w-3.5 h-3.5" data-id="element-183" />} label="Settings" data-id="element-182" />
        <NavItem icon={<HelpCircle className="w-3.5 h-3.5" data-id="element-185" />} label="Help & Feedback" data-id="element-184" />
      </div>
    </div>;
}
function NavItem({
  icon,
  label,
  active = false,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return <button onClick={onClick} aria-current={active ? 'page' : undefined} className={`w-full flex items-center px-2 py-1.5 rounded-md transition-colors group ${active ? 'bg-[#edeef0] text-slate-900 font-medium' : 'text-slate-600 hover:text-slate-900 hover:bg-[#edeef0]'}`} data-id="element-186">
      <span className={`mr-2.5 ${active ? 'text-slate-700' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-187">
        {icon}
      </span>
      <span className={active ? 'font-medium' : 'font-medium'} data-id="element-188">{label}</span>
    </button>;
}